import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { localizedRoutes } from '@/i18n/routes'

/**
 * FinalCTA — the recurring "Hai in mente un progetto?" closing band that
 * appears directly above the Footer on every page except the Contact page.
 *
 * Design: full-width dark charcoal band, dense diagonal brand-blue stripe
 * texture bleeding in from the right edge, a pulled-left headline in
 * display-weight type, a short supporting sentence, and a solid brand-blue
 * primary CTA. A fine brand-blue rule sits above the band to tie it to the
 * page content; a second rule sits below to hand off to the footer.
 *
 * The component reads the current route and hides itself on the Contact
 * page (both Italian `contatti` and English `contacts` slugs).
 */
export function FinalCTA() {
  const { t } = useTranslation()
  const location = useLocation()

  const pathSegments = location.pathname.split('/').filter(Boolean)
  const currentSlug = pathSegments[1] || ''
  const contactSlugs: string[] = [
    localizedRoutes.contacts.it,
    localizedRoutes.contacts.en,
  ]

  if (contactSlugs.includes(currentSlug)) {
    return null
  }

  return (
    <section
      aria-labelledby="final-cta-heading"
      className="relative isolate overflow-hidden bg-ink-900 text-white"
    >
      {/* Top brand rule */}
      <div
        aria-hidden="true"
        className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-brand-500 to-transparent"
      />

      {/* Diagonal brand stripe field bleeding in from the right */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-1/2 md:w-2/5 opacity-[0.12] fx-diagonal-accent"
      />

      {/* Faint brand glow behind the heading */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-32 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full bg-brand-500/20 blur-3xl"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end lg:items-center">
          <div className="lg:col-span-8">
            {/* Eyebrow — coordinate/engineering tag */}
            <p className="mb-4 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.28em] text-brand-300">
              <span aria-hidden="true" className="block h-px w-10 bg-brand-500" />
              {t('finalCta.eyebrow')}
            </p>

            <h2
              id="final-cta-heading"
              className="fx-text-balance font-bold leading-[1.05] text-3xl sm:text-4xl md:text-[2.5rem] text-white"
            >
              {t('finalCta.title')}
            </h2>

            <p className="mt-5 max-w-2xl fx-text-pretty text-base md:text-lg leading-relaxed text-white/75">
              {t('finalCta.body')}
            </p>
          </div>

          <div className="lg:col-span-4 flex lg:justify-end">
            <LocalizedLink
              to="contacts"
              className="group relative inline-flex items-center gap-3 rounded-sm bg-brand-500 px-7 py-4 text-sm md:text-base font-bold uppercase tracking-[0.08em] text-white shadow-[0_10px_30px_-12px_rgba(74,155,220,0.55)] transition-[background-color,transform,box-shadow] duration-200 hover:bg-brand-400 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900"
            >
              {/* Left tick accent */}
              <span
                aria-hidden="true"
                className="absolute -left-[3px] top-1/2 block h-6 w-[3px] -translate-y-1/2 bg-white/90 transition-all duration-200 group-hover:h-8"
              />
              {t('finalCta.button')}
              <ArrowRight
                className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                strokeWidth={2.75}
              />
            </LocalizedLink>
          </div>
        </div>
      </div>

      {/* Bottom brand rule — hand-off to the footer */}
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-0 right-0 h-px bg-white/10"
      />
    </section>
  )
}
